import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import PostCardCompact, { type PostCardCompactProps } from '@src/components/Post/PostCard/PostCardCompact';
import { usePaginatedPosts } from '@src/store/posts/hooks/usePaginatedPosts';
import type { PostCollectionType } from '@src/store/posts/posts';
import { type FC, memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '../EmptyState';

interface PostListProps {
    tokenName: string;
    className?: string;
    type: PostCollectionType;
    userId?: string;
    fetchLimit?: number;
    postUrlTo?: PostCardCompactProps['urlTo'];
    resetPostCollectionOnUnmount?: boolean;
    postCardCompactProps?: Omit<PostCardCompactProps, 'postId' | 'tokenName'>;
}

const PostList: FC<PostListProps> = props => {
    const { tokenName, className = '', type, userId, fetchLimit, resetPostCollectionOnUnmount } = props;
    const { postIds, loadMore, loadStatus, hasMore } = usePaginatedPosts({ type, tokenName, userId, fetchLimit, includeMyReactions: true, resetPostCollectionOnUnmount });
    const { t } = useTranslation();

    useEffect(() => {
        if (postIds.length > 0) return;
        loadMore();
    }, [type]);

    const onBackToTopClick = useCallback(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    function renderLoadMoreButton() {
        if (!hasMore) return;

        return (
            <Button isLoading={loadStatus === 'loading'} size="md" className="w-full" onClick={loadMore}>
                <Text>{t('postList.loadMoreButton')}</Text>
            </Button>
        );
    }

    function renderBackToTopButton() {
        if (!hasMore) return;

        return (
            <Button color="base" size="md" className="w-full" onClick={onBackToTopClick}>
                <Text>{t('postList.backToTopButton')}</Text>
            </Button>
        );
    }

    function renderPost(postId: string) {
        const { postUrlTo, postCardCompactProps } = props;
        return (
            <PostCardCompact urlTo={postUrlTo} key={postId} tokenName={tokenName} className={cn('bg-transparent dark:bg-transparent')} postId={postId} {...postCardCompactProps} />
        );
    }

    function renderEmptyState() {
        if (loadStatus !== 'success' || postIds.length > 0) return null;
        return <EmptyState context="post" type={type} />;
    }

    function renderPosts() {
        return <div className="grid grid-cols-1 gap-4">{postIds.map(renderPost)}</div>;
    }

    return (
        <div className={cn('space-y-4', className)}>
            {renderEmptyState()}
            {renderPosts()}
            <div className="flex max-w-md mx-auto space-x-4">
                {renderLoadMoreButton()}
                {renderBackToTopButton()}
            </div>
        </div>
    );
};

export default memo(PostList);
